/**
 * widgets/registry.orchestrator.tsx — widgety orchestrátoru.
 * Environments list (gradient karty) + summary stats pro My Panel / node workspace.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Activity, Boxes, AlertCircle, CheckCircle2, Layers } from 'lucide-react';
import { StatusDot } from '../components/ui/StatusDot';
import { Badge } from '../components/ui/Badge';
import { StatCard, StatsGrid } from '../components/ui/Stats';
import { listWidget } from './templates';
import type { WidgetRenderProps, WidgetSpec } from './types';
import { callWidgetTool } from './scope';
import { envIsLocalToListedNode } from '../pages/modules/orchestrator-scope';

interface OrchEnv {
  name: string;
  status: string;
  services: number;
  running: string; // "3/3"
  target_node?: string | null;
  primary_node?: string | null;
  is_local?: boolean;
  last_apply?: string;
  codable_id?: string | null;
}

function envTone(status: string): { dot: string; grad: string } {
  const s = (status || '').toLowerCase();
  if (s === 'applied' || s === 'running') {
    return { dot: 'running', grad: 'from-emerald-500/10' };
  }
  if (s === 'error' || s === 'failed') return { dot: 'error', grad: 'from-red-500/10' };
  if (s === 'provisioning' || s === 'pending' || s === 'applying') {
    return { dot: 'pending', grad: 'from-yellow-500/10' };
  }
  return { dot: 'stopped', grad: 'from-gray-500/5' };
}

function OrchEnvironmentsList(props: WidgetRenderProps) {
  const { t } = useTranslation();
  const scope = props.scope ?? { node: null, identity: null };
  const nodeKey = scope.node ?? 'local';

  // listWidget already handles data — this custom component is for phone body
  // and full control. Keep using orch_list via callWidgetTool for node-aware scope.
  const q = useQuery({
    queryKey: ['orch-widget-envs', nodeKey],
    queryFn: () =>
      callWidgetTool<{ ok?: boolean; environments?: OrchEnv[]; local_node?: string }>(
        'orchestrator.orch_list',
        {},
        scope,
      ),
    staleTime: 20_000,
  });

  const envs = (q.data?.environments ?? []).filter((e) =>
    envIsLocalToListedNode(e, q.data?.local_node ?? '')
  );
  if (q.isLoading) {
    return <div className='text-xs text-gray-500 py-4 text-center'>{t('common.loading')}</div>;
  }
  if (envs.length === 0) {
    return (
      <div className='text-xs text-gray-600 py-6 text-center'>
        {t('orchestrator.widgetEnvsEmpty')}
      </div>
    );
  }

  return (
    <div className='space-y-1 max-h-[min(24rem,55vh)] overflow-auto'>
      {envs.map((e) => {
        const tone = envTone(e.status);
        const [run, total] = (e.running || '0/0').split('/').map(Number);
        const pct = total > 0 ? Math.round((run / total) * 100) : 0;
        return (
          <a
            key={e.name}
            href={`/modules/orchestrator?env=${encodeURIComponent(e.name)}`}
            className={`flex items-center gap-2 px-2 py-1.5 rounded-lg bg-gradient-to-r ${tone.grad} to-transparent hover:bg-gray-900/40 transition-colors`}
          >
            <StatusDot status={tone.dot} />
            <span className='text-sm text-gray-100 font-medium truncate flex-1'>{e.name}</span>
            {e.codable_id && <Badge variant='purple' className='text-[9px]'>Codable</Badge>}
            {(e.primary_node || e.target_node) && (
              <Badge variant={e.is_local === false ? 'purple' : 'gray'} className='text-[9px]'>
                {(e.primary_node || e.target_node) === 'local'
                  ? t('orchestrator.local')
                  : (e.primary_node || e.target_node)}
              </Badge>
            )}
            <div className='w-12 h-1.5 rounded-full bg-gray-800 overflow-hidden shrink-0'>
              <div
                className={`h-full ${pct === 100 ? 'bg-emerald-500' : pct > 0 ? 'bg-yellow-500' : 'bg-gray-600'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className='text-[10px] text-gray-500 tabular-nums w-8 text-right shrink-0'>
              {e.running || '—'}
            </span>
          </a>
        );
      })}
    </div>
  );
}

function OrchSummaryWidget(props: WidgetRenderProps) {
  const { t } = useTranslation();
  const scope = props.scope ?? { node: null, identity: null };
  const nodeKey = scope.node ?? 'local';
  const q = useQuery({
    queryKey: ['orch-widget-summary', nodeKey],
    queryFn: () =>
      callWidgetTool<{ ok?: boolean; environments?: OrchEnv[]; local_node?: string }>(
        'orchestrator.orch_list',
        {},
        scope,
      ),
    staleTime: 20_000,
  });

  const stats = useMemo(() => {
    const envs = (q.data?.environments ?? []).filter((e) =>
      envIsLocalToListedNode(e, q.data?.local_node ?? '')
    );
    let running = 0;
    let error = 0;
    let other = 0;
    let codable = 0;
    for (const e of envs) {
      if (e.codable_id) codable++;
      const s = (e.status || '').toLowerCase();
      if (s === 'running' || s === 'applied') running++;
      else if (s === 'error' || s === 'partial' || s === 'destroying') error++;
      else other++;
    }
    return { total: envs.length, running, error, other, codable };
  }, [q.data?.environments, q.data?.local_node]);

  if (q.isLoading) {
    return <div className='text-xs text-gray-500 py-4 text-center'>{t('common.loading')}</div>;
  }

  return (
    <div className='space-y-3'>
      <StatsGrid columns={2} className='gap-2'>
        <StatCard
          label={t('orchestrator.statRunning', 'Běží')}
          value={stats.running}
          icon={CheckCircle2}
          variant='success'
          className='p-3'
        />
        <StatCard
          label={t('orchestrator.statError', 'Chyba')}
          value={stats.error}
          icon={AlertCircle}
          variant={stats.error ? 'error' : 'default'}
          className='p-3'
        />
        <StatCard
          label={t('orchestrator.statTotal', 'Celkem')}
          value={stats.total}
          icon={Layers}
          className='p-3'
        />
        <StatCard
          label={t('phone.orch.statCodable', 'Codable')}
          value={stats.codable}
          icon={Activity}
          variant='info'
          className='p-3'
        />
      </StatsGrid>
      <a
        href='/modules/orchestrator'
        className='block text-center text-xs text-blue-400 hover:text-blue-300 py-1'
      >
        {t('phone.orch.full')}
      </a>
    </div>
  );
}

export const ORCHESTRATOR_WIDGETS: WidgetSpec[] = [
  listWidget<OrchEnv>({
    id: 'orchestrator.environments',
    title: 'Environments',
    module: 'orchestrator',
    tool: 'orchestrator.orch_list',
    items: 'environments',
    icon: Boxes,
    format: 'lg',
    formats: ['md', 'lg'],
    limit: { mobile: 6, desktop: 16 },
    emptyLabel: 'No environments',
    row: (e) => {
      const tone = envTone(e.status);
      const [run, total] = (e.running || '0/0').split('/').map(Number);
      const pct = total > 0 ? Math.round((run / total) * 100) : 0;
      return (
        <div
          className={`flex items-center gap-2 -mx-3 px-3 py-0.5 bg-gradient-to-r ${tone.grad} to-transparent rounded`}
        >
          <StatusDot status={tone.dot} />
          <span className='text-gray-100 font-medium truncate'>{e.name}</span>
          {(e.primary_node || e.target_node) && (
            <Badge variant={e.is_local === false ? 'purple' : 'gray'}>
              {(e.primary_node || e.target_node) === 'local'
                ? 'local'
                : (e.primary_node || e.target_node)}
            </Badge>
          )}
          <div className='ml-auto flex items-center gap-2'>
            <div className='w-14 h-1.5 rounded-full bg-gray-800 overflow-hidden'>
              <div
                className={`h-full ${pct === 100 ? 'bg-emerald-500' : pct > 0 ? 'bg-yellow-500' : 'bg-gray-600'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className='text-xs text-gray-400 tabular-nums w-8 text-right'>{e.running}</span>
          </div>
        </div>
      );
    },
  }),
  {
    id: 'orchestrator.summary',
    title: 'Orchestrator summary',
    module: 'orchestrator',
    kind: 'custom',
    format: 'md',
    formats: ['sm', 'md', 'lg'],
    icon: Activity,
    since: '2.1.0',
    component: OrchSummaryWidget,
    phone: OrchSummaryWidget,
  },
  {
    id: 'orchestrator.environments-links',
    title: 'Environments',
    module: 'orchestrator',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: Boxes,
    since: '2.1.0',
    component: OrchEnvironmentsList,
    phone: OrchEnvironmentsList,
  },
];
